import React, {useState, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {Box, Divider, Typography} from '@mui/material';
import {LocationOnOutlined, SpaceDashboardOutlined} from '@mui/icons-material';
import { LOCAL_STORAGE, LOCKER_TRANSACTION_REF, LS_LOCKER_BANK_INFO, LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS, PAYMENT_GATEWAY_REFERENCE, PAYMENT_REFERENCE_ID, SELECTED_EQUPMNENT, SESSION_STORAGE, SS_PAYMENT_GATEWAY } from '../assets/constants/BrowserStorageKeys';
import {getSecureItemFromSpecificStorage} from '../lib/BrowserStorageAccessMiddleware';
import CustomSliderComponent from '../components/CustomSliderComponent';
import { fetchPaymentStatus, getSessionStatus, openLocker, openLockerDoor, releaseLockerOfPickUpUser, rentProductAndReserveLocker } from '../api/api';
import { BUY, ERROR, SUCCESS, TOAST_ERROR, TOAST_SUCCESS, BUTTON_WARNING, MANUAL } from '../assets/constants/Constants';
import {HOME, LANDING, EQUIPMENT_DETAILS, PAYMENT_DETAILS_VERIFIED, LOCKER_OPENED, EQUIPMENT_GRID} from '../assets/constants/PageList';
import { formatPriceWithCurrencyCode, formatRentalDurationFromMinutes, getMinAndMaxPriceObjects, getPathForNextPage, verifyUserProximity, getUnitLabelParams } from '../lib/Utils';
import CircularProgressLoader from '../components/CircularProgressLoader';
import { useTranslation } from 'react-i18next';
import CustomDialog from '../components/CustomDialog';
import ShowToast from '../components/ToastComponent';

const VerificationSuccessfulPage = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const lockerBankInfo = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LS_LOCKER_BANK_INFO);
    const unitLabelParams = getUnitLabelParams(lockerBankInfo?.LockerBankType ?? lockerBankInfo?.bank_type, t);
    const [loadingMessage, setLoadingMessage] = useState('Loading. Please wait.');
    const [isLoading, setIsLoading] = useState(false);
    const [userProximityError, setUserProximityError] = useState('');
    const [isUserProximityErrorModalVisible, setIsUserProximityErrorModalVisible] = useState(false);
    const payment_gateway_reference = getSecureItemFromSpecificStorage(LOCAL_STORAGE, PAYMENT_GATEWAY_REFERENCE);
    const payment_reference_id = getSecureItemFromSpecificStorage(LOCAL_STORAGE, PAYMENT_REFERENCE_ID);
    const equipment = getSecureItemFromSpecificStorage(LOCAL_STORAGE, SELECTED_EQUPMNENT);
    const paymentGatewaySource = getSecureItemFromSpecificStorage(SESSION_STORAGE, SS_PAYMENT_GATEWAY);
    const locker_transaction_ref = getSecureItemFromSpecificStorage(LOCAL_STORAGE, LOCKER_TRANSACTION_REF);
    const assignmentSettings = getSecureItemFromSpecificStorage(LOCAL_STORAGE,LS_TENANT_ASSIGNMENT_LOCKER_BANK_SETTINGS);
    const isGeoFencingEnabled = assignmentSettings?.is_geofencing;
    const maxRentalSlab = getMinAndMaxPriceObjects(equipment)?.max;
    const productName = equipment?.title || 'product';
    const authorizedHoldAmount = formatPriceWithCurrencyCode(equipment?.product_price ?? maxRentalSlab?.rent_price, equipment?.currency || maxRentalSlab?.currency);
    const purchaseAmount = formatPriceWithCurrencyCode(equipment?.product_price, equipment?.currency);
    const maxRentalDuration = formatRentalDurationFromMinutes(maxRentalSlab?.rent_minute) || 'the maximum rental duration';

    useEffect(()=> {
       if (equipment?.product_type !== BUY) {
           getPaymentSessionStatus();
       }
    }, [])

    const getPaymentSessionStatus = async () => {
        try {
            setIsLoading(true);
            if(payment_gateway_reference && payment_reference_id){
                const response = await fetchPaymentStatus(payment_reference_id , payment_gateway_reference);
                if(response.data?.is_paid && response.data?.capture_mode === MANUAL){
                    setIsLoading(false);
                } else{
                    ShowToast(t(response.message), TOAST_ERROR);
                    navigateToEquipmentDetails(equipment);
                }
            }
        } catch (error){
            console.info("Error in getPaymentSessionStatus :", error);
            ShowToast("Failed to verify the payment status.", TOAST_ERROR);
            navigateToEquipmentDetails(equipment);
        } finally {
            setIsLoading(false);
        }
    }

    const openAndReleaseLockerForPurchase = async () => {
        try {
            setLoadingMessage(t('opening_locker_please_wait', unitLabelParams));
            setIsLoading(true);
            const openLockerResponse = await openLocker(locker_transaction_ref);
            if (openLockerResponse?.status === SUCCESS) {
                setLoadingMessage(t('lockerReleasing', unitLabelParams));
                const releaseLockerResponse = await releaseLockerOfPickUpUser(locker_transaction_ref);
                if (releaseLockerResponse?.status === SUCCESS) {
                    setIsLoading(false);
                    ShowToast(t('lockerOpendAndReleased', unitLabelParams), TOAST_SUCCESS);
                    history.push({
                        pathname: getPathForNextPage(PAYMENT_DETAILS_VERIFIED, LOCKER_OPENED),
                        state: { lockerResponse: openLockerResponse }
                    });
                } else {
                    setIsLoading(false);
                    ShowToast(t(releaseLockerResponse?.message), TOAST_ERROR);
                }
            } else {
                setIsLoading(false);
                ShowToast(t(openLockerResponse?.message), TOAST_ERROR);
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error while opening/releasing locker for purchase:', error);
            ShowToast(
                error?.response?.data?.message || t('errorFailedToOpenLocker', unitLabelParams),
                TOAST_ERROR
            );
        }
    }

    const verifyPaymentAndOpenLockerForPurchase = async () => {
        try {
            setLoadingMessage(t('verifyingPaymentStatusPleaseWait'));
            setIsLoading(true);
            const fetchPaymentStatusResponse = await fetchPaymentStatus(payment_reference_id, paymentGatewaySource);
            if (fetchPaymentStatusResponse?.data?.is_paid) {
                    await openAndReleaseLockerForPurchase();
            } else {
                setIsLoading(false);
                ShowToast(t(fetchPaymentStatusResponse?.message), TOAST_ERROR);
                navigateToEquipmentDetails(equipment);
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error while verifying payment and opening locker for purchase:', error);
            navigateToEquipmentDetails(equipment);
        }
    }

    const slideToOpenButtonHandler = async () => {
        try {
            if (equipment?.product_type === BUY) {
                await verifyPaymentAndOpenLockerForPurchase();
            } else {
                await proceedToRentProduct();
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error while opening locker:', error);
            ShowToast(t('errorFailedToOpenLocker', unitLabelParams), TOAST_ERROR);
        }
    }

    useEffect(() => {
        const unblock = history.listen((location, action) => {
          if (action === 'POP') {
            history.push(getPathForNextPage(PAYMENT_DETAILS_VERIFIED, HOME));
          }
        });
    
        return () => {
          unblock();
        };
      }, [history]);


    const navigateToEquipmentDetails = (equipment) => {
		history.push({
			pathname: getPathForNextPage(PAYMENT_DETAILS_VERIFIED, EQUIPMENT_GRID),
			state: {
				selectedEquipment: equipment
			}
		});
	}

    const proceedToRentProduct = async  () => {
        try {
            setLoadingMessage(t('opening_locker_please_wait', unitLabelParams));
            setIsLoading(true);

            if (isGeoFencingEnabled) {
                const maxAllowedDistanceInMeters = assignmentSettings?.geofencing_radius;
                const isUserNearbyLockerBank = await verifyUserProximity({latitude: lockerBankInfo.latitude, longitude: lockerBankInfo.longitude}, maxAllowedDistanceInMeters);

                if ( !isUserNearbyLockerBank.isNearby ) {
                    setLoadingMessage('');
                    setIsLoading(false);
                    setUserProximityError(isUserNearbyLockerBank.message);
                    setIsUserProximityErrorModalVisible(true);

                    return;
                }
            }

            let assignmentSlug = assignmentSettings?.slug;
            const response = await rentProductAndReserveLocker(assignmentSlug, equipment?.id, equipment?.pricing_info[0].id, payment_reference_id );

            if (response.status === SUCCESS) {
                const lockerResponse = response?.data;
                ShowToast(t('productRentedSuccessMsg', TOAST_SUCCESS));

                const openLockerResponse = await openLockerDoor(lockerResponse.ref_id);

                if (openLockerResponse.status === SUCCESS) {
                    setIsLoading(false);
                    history.push({pathname : getPathForNextPage(PAYMENT_DETAILS_VERIFIED, LOCKER_OPENED),state : {lockerResponse : lockerResponse}});
                } else {
                    setIsLoading(false);
                    ShowToast(t(openLockerResponse?.message || 'errorFailedToOpenLocker', unitLabelParams), TOAST_ERROR);
                }
            } else {
                setIsLoading(false);
                ShowToast(t(response?.message || 'errorFailedToOpenLocker', unitLabelParams), TOAST_ERROR);
                navigateToEquipmentDetails(equipment);
            }
        } catch (error) {
            setIsLoading(false);
            console.error('Error while renting product and opening locker:', error);
            ShowToast(error?.response?.data?.message || t('errorFailedToOpenLocker', unitLabelParams), TOAST_ERROR);
        }
    }

    return (
        <Box className='verification-successful-page'>
            <Box className='main-container'>
                {isLoading && (
                    <Box className="main">
                        <CircularProgressLoader message={loadingMessage} />
                    </Box>
                )}
                {!isLoading && (
                <Box className='main'>
                    <Box className='img-container'>
                        <img className='verification-successful-img' src={require('../assets/images/person_with_circled_checkmark.png')} alt='Verification Successful Image' />
                    </Box>
                    <Box className='title-and-description-container'>
                        <Typography className='title'>{equipment?.product_type === BUY ? t('successfulPaymentText') : t('verificationSuccessText')}</Typography>
                        <Typography className='description'>
                            {equipment?.product_type === BUY ? t('purchaseSuccessSubheading', unitLabelParams) : t('verificationSuccessSubheading')}
                        </Typography>
                    </Box>
                    {equipment?.product_type !== BUY && <>
                    <Divider id='verification-title-and-rental-info-divider' />
                    <Box className='rental-hold-info-container'>
                        <Typography className='authorized-hold-text'>
                            Authorised hold: {authorizedHoldAmount}
                        </Typography>
                        <Box component='ol' className='rental-hold-instructions'>
                            <Typography component='li' className='instruction-text'>
                                Return the {productName} and the hold is released in full, usually within minutes.
                            </Typography>
                            <Typography component='li' className='instruction-text'>
                                You are charged only for the booking period.
                            </Typography>
                            <Typography component='li' className='instruction-text'>
                                If the {productName} is not returned within {maxRentalDuration}, the full hold amount is debited.
                            </Typography>
                        </Box>
                    </Box>
                    <Box className='product-rental-info-container'>
                        <Box className='info-container'>
                            <Box className='icon-container'>
                                <SpaceDashboardOutlined className='product-info-icon' />
                            </Box>
                            <Box className='title-and-value-container'>
                                <Box className='title'>{t('productRentedText')}</Box>
                                <Box className='value'>{equipment?.title}</Box>
                            </Box>
                        </Box>
                        <Box className='info-container'>
                            <Box className='icon-container'>
                                <LocationOnOutlined className='product-info-icon' />
                            </Box>
                            <Box className='title-and-value-container'>
                                <Box className='title'>{t('unitLocationText')}</Box>
                                <Box className='value'>{equipment?.door_details?.locker_bank_details?.bank_name}</Box>
                            </Box>
                        </Box>
                    </Box>
                    </>}
                    {equipment?.product_type === BUY && <>
                    <Divider id='verification-title-and-rental-info-divider' />
                    <Box className='product-rental-info-container'>
                        <Box className='info-container'>
                            <Box className='icon-container'>
                                <SpaceDashboardOutlined className='product-info-icon' />
                            </Box>
                            <Box className='title-and-value-container'>
                                <Box className='title'>{t('productPurchasedText')}</Box>
                                <Box className='value'>{equipment?.title}</Box>
                            </Box>
                        </Box>
                        {purchaseAmount && (
                        <Box className='info-container'>
                            <Box className='icon-container'>
                                <SpaceDashboardOutlined className='product-info-icon' />
                            </Box>
                            <Box className='title-and-value-container'>
                                <Box className='title'>{t('amountPaidText')}</Box>
                                <Box className='value'>{purchaseAmount}</Box>
                            </Box>
                        </Box>
                        )}
                        <Box className='info-container'>
                            <Box className='icon-container'>
                                <LocationOnOutlined className='product-info-icon' />
                            </Box>
                            <Box className='title-and-value-container'>
                                <Box className='title'>{t('unitLocationText')}</Box>
                                <Box className='value'>{equipment?.door_details?.locker_bank_details?.bank_name}</Box>
                            </Box>
                        </Box>
                    </Box>
                    </>}
                    <Box className="slide-to-open-button-container">
                        <CustomSliderComponent
                            label={t('slideToOpenText')}
                            bankDetails={null}
                            overlayText={" "}
                            onSlideDone={slideToOpenButtonHandler}
                            extraClass={'slide-to-open-button'}
                        />
                    </Box>
                    <CustomDialog
                        dialogId='user-proximity-error-modal'
                        dialogVisible={isUserProximityErrorModalVisible}
                        onClose={() => setIsUserProximityErrorModalVisible(false)}
                        handleCancel={() => setIsUserProximityErrorModalVisible(false)}
                        dialogTitle={t('Location Error')}
                        dialogContentText={userProximityError}
                        buttonOneTitle={t('Ok')}
                        buttonProps={{buttonOne: {type: BUTTON_WARNING}}}
                    />
                </Box>
                )}
            </Box>
        </Box>
    );
};

export default VerificationSuccessfulPage;
